// Masking of the sales offer activation token before Sentry and PostHog (SALES-16, MON-01, R09). Pure: no Angular, no
// rxjs, no SDK import (used by src/main.ts). The token appears in the page path /activation/<token>, in the API paths
// /api/v1/billing/offers/<token>…, and URL-encoded in login redirects (returnUrl=%2Factivation%2F<token>).

export const TOKEN_PLACEHOLDER = ':token';

const SLASH = '(?:/|%2f|%252f)';
const TOKEN_SEGMENT = new RegExp(`(${SLASH}(?:activation|billing${SLASH}offers)${SLASH})[A-Za-z0-9_-]+`, 'gi');
const MAX_DEPTH = 20;

export function scrubActivationToken(value: string): string {
  return value.replace(TOKEN_SEGMENT, `$1${TOKEN_PLACEHOLDER}`);
}

function isPlainObject(value: any): boolean {
  if (!value || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

// Copy on write: containers are copied only when one of their strings changes; anything else is returned as is. A
// container reached twice gets the same result; a cycle is left as is.
function scrubValue(value: any, depth: number, done: WeakMap<object, any>): any {
  if (typeof value === 'string') return scrubActivationToken(value);
  if (depth > MAX_DEPTH || !(Array.isArray(value) || isPlainObject(value))) return value;
  if (done.has(value)) return done.get(value);
  done.set(value, value);
  let copy: any = null;
  for (const key of Object.keys(value)) {
    const before = value[key];
    const after = scrubValue(before, depth + 1, done);
    if (after !== before) {
      if (!copy) copy = Array.isArray(value) ? value.slice() : { ...value };
      copy[key] = after;
    }
  }
  const result = copy || value;
  done.set(value, result);
  return result;
}

/** Every string of a plain JSON-like value, token masked; same reference when nothing changes. */
export function scrubDeep<T>(value: T): T {
  return scrubValue(value, 0, new WeakMap());
}

/** Sentry beforeSend / beforeSendTransaction: URL, headers, transaction, messages, exceptions, breadcrumbs, spans. */
export function scrubSentryEvent<T>(event: T): T {
  return scrubDeep(event);
}

/** Sentry beforeBreadcrumb: navigation, xhr, fetch and console breadcrumbs. */
export function scrubSentryBreadcrumb<T>(breadcrumb: T): T {
  return scrubDeep(breadcrumb);
}

/** PostHog before_send: properties ($current_url, $pathname, $referrer…), $set, $set_once and session snapshots. */
export function scrubPosthogEvent<T>(event: T): T {
  return scrubDeep(event);
}
